import { useUpdateEffect } from 'ahooks';
import React from 'react';
import { shallowEqual } from 'react-redux';
import { Box } from '@apia/theme';
import { useAppSelector } from '../../../hooks/hooks';
import { customEvents } from '../../../utils/dom';
import { usePrintContext } from '../../print/execution/Task';
import AccordionElement from '../AccordionElement';
import { TabsContext } from './Render';
import TabsController, { TTab } from './TabsController';

const TabRender = ({
  children,
  id,
}: {
  id: TTab['id'];
  children: React.ReactNode;
}) => {
  const [ref, setRef] = React.useState<HTMLDivElement | undefined>(undefined);
  const { isAccordion, label, props, isOpen, isDefaultCollapsed } =
    useAppSelector((global) => {
      const thisTab = global.informationSlice.tabs.list.find(
        (tab) => tab.id === id,
      );
      return {
        isAccordion: thisTab?.asaccordion,
        label: thisTab?.label,
        props: thisTab?.props,
        isOpen: thisTab?.open,
        isDefaultCollapsed: !!thisTab?.defaultCollapsed,
      };
    }, shallowEqual);
  const [isExpanded, setExpanded] = React.useState(!isDefaultCollapsed);

  const contextValue = React.useMemo(
    () => ({
      close: (shouldFocus = true) =>
        TabsController.toggle(id, true, shouldFocus),
      open: (shouldFocus = true) =>
        TabsController.toggle(id, true, shouldFocus),
      tabId: id,
    }),
    [id],
  );
  useUpdateEffect(() => {
    const handleCustomFocus = () => {
      if (!TabsController.getTabById(id)?.open)
        TabsController.openTab(id, false);
    };
    ref?.addEventListener(customEvents.focus, handleCustomFocus);
    return () => {
      ref?.removeEventListener(customEvents.focus, handleCustomFocus);
    };
  }, [ref]);

  const isPrinting = usePrintContext();

  if (label === undefined) return null;

  return (
    <TabsContext.Provider value={contextValue}>
      <Box
        className="tabs__render__child"
        ref={setRef}
        data-testid={`tabRender__${id}`}
        sx={{ display: isOpen || isPrinting ? 'block' : 'none' }}
      >
        {isAccordion && (
          <AccordionElement
            expanded={isExpanded ?? true}
            onToggle={() => {
              setExpanded((current) => !current);
            }}
            id={String(id)}
            title={label}
            {...window.getVariant('tab')}
            {...props}
          >
            {children}
          </AccordionElement>
        )}
        {!isAccordion && <Box {...props}>{children}</Box>}
      </Box>
    </TabsContext.Provider>
  );
};

export default TabRender;
